/**
 * CRM Lead Detail page
 * Shows lead info, FCC license data, notes, and linked tasks.
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { formatLocalDateTime, formatLocalDate } from '../../utils/formatDateTime'
import {
  getTrackedLead,
  updateTrackedLead,
  deleteTrackedLead,
  convertLead,
  addLeadNote,
  deleteLeadNote,
  listTasks,
  createTask,
  updateTask,
  getLeadFccParties,
  getLeadDncStatus,
} from '../../services/crm'
import { LEAD_WORK_STATES } from '../../services/crm'
import type {
  ClientFCCParty, LeadWorkState, TrackedLead, TrackedLeadNote,
} from '../../services/crm'
import EntityCard from '../../components/EntityCard'
import type { Task, TaskCreate, TaskPriority, TaskStatus } from '../../types'
import { RADIO_SERVICES } from '../../types'
import { listUsers } from '../../services/users'
import UserSelect from '../../components/UserSelect'
import { type UserOption as SharedUserOption } from '../../utils/userOptions'
import { XIcon } from '../../components/Icons'
import SendCardModal from '../../components/SendCardModal'
import SendChooser from '../../components/SendChooser'
import SendMessageModal from '../../components/SendMessageModal'
import ScheduledMessagesPanel from '../../components/ScheduledMessagesPanel'
import DncToggle, { DncBadge } from '../../components/DncToggle'
import type { DncStatus } from '../../types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
}

/** Muted while nobody has picked the lead up, coloured once somebody has. */
const WORK_STATE_BADGE: Record<LeadWorkState, string> = {
  new: 'bg-gray-100 text-gray-500',
  working: 'bg-amber-100 text-amber-800',
  waiting: 'bg-sky-100 text-sky-800',
}

const WORK_STATE_LABEL: Record<LeadWorkState, string> = Object.fromEntries(
  LEAD_WORK_STATES.map((w) => [w.value, w.label]),
) as Record<LeadWorkState, string>

const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

function expirationColor(days: number | null): string {
  if (days === null) return 'text-gray-500'
  if (days < 30) return 'text-red-600 font-semibold'
  if (days < 60) return 'text-orange-500 font-medium'
  return 'text-green-600'
}

type UserOption = SharedUserOption

/** Label + value row with a fixed-width label so fields line up consistently. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 text-xs uppercase shrink-0 w-16 pt-0.5">{label}</span>
      <span className="text-gray-700 min-w-0 flex-1">{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>()
  const navigate = useNavigate()
  const id = Number(leadId)

  const [lead, setLead] = useState<TrackedLead | null>(null)
  /** The licensee and filing agent on this lead's license, read fresh from FCC data. */
  const [fccParties, setFccParties] = useState<ClientFCCParty[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Notes
  const [notes, setNotes] = useState<TrackedLeadNote[]>([])
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [noteError, setNoteError] = useState('')

  // Tasks
  const [tasks, setTasks] = useState<Task[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [showAddTask, setShowAddTask] = useState(false)
  const [taskForm, setTaskForm] = useState<{ title: string; due_at: string; priority: TaskPriority; assigned_to_user_id: string }>({
    title: '', due_at: '', priority: 'medium', assigned_to_user_id: '',
  })
  const [taskError, setTaskError] = useState('')
  const [savingTask, setSavingTask] = useState(false)

  // Actions
  const [converting, setConverting] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [actionError, setActionError] = useState('')
  const [users, setUsers] = useState<UserOption[]>([])
  const [showSendCard, setShowSendCard] = useState(false)
  // One "Send" button on the action row, not one per kind of thing. It used to
  // say "Send Card" and could only do that, which left the messages — including
  // the reference copy — reachable only from a link in the Scheduled Messages
  // panel further down, where nobody looked for them. Send Card is now an item
  // in that menu rather than a separate button: a card e-mail has its own
  // layout, and sending one from a lead page is what writes the lead note.
  const [showSendMessage, setShowSendMessage] = useState(false)
  // Which message the Send menu picked, so the dialog opens on it. Null when the
  // dialog is opened without a choice, from the Scheduled Messages panel.
  const [sendTemplateId, setSendTemplateId] = useState<number | null>(null)
  const [scheduledRefresh, setScheduledRefresh] = useState(0)

  // Our own contact details for this lead, edited inline.
  const [editingContact, setEditingContact] = useState(false)
  const [contactSaving, setContactSaving] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  const [contactForm, setContactForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '',
  })

  // Whether this lead is on the Do Not Contact list. Shown on the record itself,
  // not only inside the send dialog — "may we contact these people" is something
  // you want to know while reading the lead, not only once you have started
  // writing to them.
  const [dncStatus, setDncStatus] = useState<DncStatus | null>(null)
  const [dncError, setDncError] = useState('')

  useEffect(() => {
    loadLead()
    loadFccParties()
    loadTasks()
    void loadDncStatus()
    listUsers().then((res) => setUsers(res.users)).catch(() => {})
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadDncStatus() {
    try {
      setDncStatus(await getLeadDncStatus(id))
    } catch {
      // Display-only: leave the badge off rather than breaking the page.
      setDncStatus(null)
    }
  }

  async function loadLead() {
    setLoading(true)
    setError('')
    try {
      const data = await getTrackedLead(id)
      setLead(data)
      setNotes(data.notes)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lead')
    } finally {
      setLoading(false)
    }
  }

  async function loadFccParties() {
    try {
      setFccParties(await getLeadFccParties(id))
    } catch {
      // Display-only: a failure leaves the block out rather than breaking the page.
      setFccParties([])
    }
  }

  async function loadTasks() {
    setLoadingTasks(true)
    try {
      const res = await listTasks({ lead_id: id, page_size: 50 })
      setTasks(res.items)
    } catch { /* non-critical */ }
    finally { setLoadingTasks(false) }
  }

  async function handleAddNote() {
    if (!newNote.trim()) return
    setAddingNote(true)
    setNoteError('')
    try {
      const note = await addLeadNote(id, newNote.trim())
      setNotes([...notes, note])
      setNewNote('')
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to add note')
    } finally {
      setAddingNote(false)
    }
  }

  async function handleDeleteNote(noteId: number) {
    try {
      await deleteLeadNote(id, noteId)
      setNotes(notes.filter((n) => n.id !== noteId))
    } catch { /* ignore */ }
  }

  async function handleAddTask(e: React.FormEvent) {
    e.preventDefault()
    if (!taskForm.title.trim()) { setTaskError('Title is required'); return }
    setSavingTask(true)
    setTaskError('')
    try {
      const payload: TaskCreate = {
        title: taskForm.title.trim(),
        priority: taskForm.priority,
        lead_id: id,
      }
      if (taskForm.due_at) {
        payload.due_at = taskForm.due_at + ':00'
        payload.due_date = taskForm.due_at.slice(0, 10)
      }
      if (taskForm.assigned_to_user_id) payload.assigned_to_user_id = parseInt(taskForm.assigned_to_user_id)
      const created = await createTask(payload)
      setTasks([...tasks, created])
      setTaskForm({ title: '', due_at: '', priority: 'medium', assigned_to_user_id: '' })
      setShowAddTask(false)
    } catch (e) {
      setTaskError(e instanceof Error ? e.message : 'Failed to create task')
    } finally {
      setSavingTask(false)
    }
  }

  async function handleTaskStatusChange(task: Task, newStatus: TaskStatus) {
    try {
      const updated = await updateTask(task.id, { status: newStatus })
      setTasks(tasks.map((t) => (t.id === updated.id ? updated : t)))
    } catch { /* ignore */ }
  }

  async function handleConvert() {
    if (!lead?.fcc_unique_system_identifier) return
    if (!confirm('Convert this lead to a CRM client record?')) return
    setConverting(true)
    setActionError('')
    try {
      const client = await convertLead(lead.fcc_unique_system_identifier)
      navigate(`/crm/clients/${client.id}`)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to convert')
      setConverting(false)
    }
  }

  async function handleDismiss() {
    if (!confirm('Dismiss this lead?')) return
    setDismissing(true)
    setActionError('')
    try {
      await updateTrackedLead(id, { status: 'dismissed' })
      navigate('/crm/leads')
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to dismiss')
      setDismissing(false)
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this lead and all its notes? This cannot be undone.')) return
    try {
      await deleteTrackedLead(id)
      navigate('/crm/leads')
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  async function handlePriorityChange(p: string) {
    if (!lead) return
    try {
      const updated = await updateTrackedLead(id, { priority: p })
      setLead(updated)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update priority')
    }
  }

  async function handleWorkStateChange(next: LeadWorkState) {
    if (!lead) return
    try {
      // Saved on change — no Save button, nothing reloads.
      setLead(await updateTrackedLead(id, { work_state: next }))
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update progress')
    }
  }

  async function handleAssigneeChange(userId: number | null) {
    if (!lead) return
    try {
      // null (not undefined) is what unassigns server-side.
      setLead(await updateTrackedLead(id, { assigned_to_user_id: userId }))
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to re-assign')
    }
  }

  /**
   * Save our contact override.
   *
   * An emptied field is sent as null, not "" — null is what clears the override
   * and falls back to the FCC's value, whereas an empty string would store a
   * blank that shadows it forever.
   */
  async function saveContactDetails() {
    if (!id) return
    setContactSaving(true)
    setContactError(null)
    try {
      const updated = await updateTrackedLead(id, {
        contact_name: contactForm.contact_name.trim() || null,
        contact_email: contactForm.contact_email.trim() || null,
        contact_phone: contactForm.contact_phone.trim() || null,
      })
      setLead(updated)
      setEditingContact(false)
    } catch (err) {
      setContactError(err instanceof Error ? err.message : 'Could not save contact details')
    } finally {
      setContactSaving(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // A toggle bound to the call sign should reflect the call sign, not any match:
  // a lead suppressed by its entity name is not cleared by un-flagging the call
  // sign, and a control that claims otherwise would lie about what it just did.
  const dncCallSignFlagged = Boolean(
    dncStatus?.matches.some((m) => m.match_type === 'call_sign' && m.value === lead?.call_sign)
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !lead) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        {error || 'Lead not found'}
        <button onClick={() => navigate('/crm/leads')} className="ml-4 text-sm underline">
          Back to leads
        </button>
      </div>
    )
  }

  const openTasks = tasks.filter((t) => t.status === 'open' || t.status === 'in_progress')
  const doneTasks = tasks.filter((t) => t.status === 'completed' || t.status === 'cancelled')

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/crm/leads" className="tap hover:underline">Leads</Link>
        {' / '}
        <span className="text-gray-900">{lead.entity_name || lead.call_sign}</span>
      </nav>

      {/* Lead Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{lead.entity_name || '—'}</h1>
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium capitalize ${PRIORITY_BADGE[lead.priority] ?? 'bg-gray-100 text-gray-600'}`}>
                {lead.priority}
              </span>
              {lead.status !== 'open' && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 capitalize">{lead.status}</span>
              )}
              {/* Progress reads next to priority, where the eye already is. The
                  control that changes it is in the grid below with the other
                  editable fields. */}
              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${WORK_STATE_BADGE[lead.work_state] ?? 'bg-gray-100 text-gray-600'}`}>
                {WORK_STATE_LABEL[lead.work_state] ?? lead.work_state}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <p className="text-gray-500 text-sm font-mono">{lead.call_sign}</p>
              {dncStatus?.suppressed && <DncBadge />}
              {lead.call_sign && (
                <DncToggle
                  callSign={lead.call_sign}
                  isDnc={dncCallSignFlagged}
                  onChange={() => { void loadDncStatus() }}
                  onError={setDncError}
                />
              )}
            </div>
            {dncStatus?.suppressed && (
              <p className="text-xs text-red-700 mt-1">
                Do not contact{dncStatus.matched_on ? ` — ${dncStatus.matched_on}` : ''}
                {dncStatus.reason ? `. ${dncStatus.reason}` : ''}
              </p>
            )}
            {dncError && <p className="text-xs text-red-600 mt-1" role="alert">{dncError}</p>}
          </div>

          <div className="flex gap-2 flex-wrap">
            {lead.call_sign && (
              <a
                href={`/licenses/${lead.call_sign}/reference`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                title="Open the full FCC Reference Copy in a new tab (keeps your notes here)"
              >
                Reference Copy ↗
              </a>
            )}
            <SendChooser
              onChooseCard={() => setShowSendCard(true)}
              onChooseTemplate={(templateId) => {
                setSendTemplateId(templateId)
                setShowSendMessage(true)
              }}
            />
            {lead.status === 'open' && (
              <>
                <button
                  onClick={handleConvert}
                  disabled={converting}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {converting ? 'Converting…' : 'Convert to Client'}
                </button>
                <button
                  onClick={handleDismiss}
                  disabled={dismissing}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {dismissing ? 'Dismissing…' : 'Dismiss'}
                </button>
              </>
            )}
            {lead.converted_to_client_id && (
              <Link
                to={`/crm/clients/${lead.converted_to_client_id}`}
                className="px-3 py-1.5 text-sm border border-primary-300 text-primary-700 rounded-md hover:bg-primary-50"
              >
                View Client →
              </Link>
            )}
            <button
              onClick={handleDelete}
              className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>

        {actionError && (
          <p className="mt-2 text-sm text-red-600">{actionError}</p>
        )}

        {/* FCC License Data */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm border-t pt-4">
          <div>
            <span className="text-gray-500 text-xs uppercase">Service</span>
            <p className="text-gray-900">{RADIO_SERVICES[lead.radio_service_code ?? ''] || lead.radio_service_code || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Transmitter</span>
            <p className="text-gray-900">{[lead.transmitter_city, lead.transmitter_state].filter(Boolean).join(', ') || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Expires</span>
            <p className={lead.expired_date ? expirationColor(lead.days_until_expiration) : 'text-gray-400'}>
              {lead.expired_date ? formatLocalDate(lead.expired_date) : '—'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Days Left</span>
            <p className={lead.days_until_expiration !== null ? expirationColor(lead.days_until_expiration) : 'text-gray-400'}>
              {lead.days_until_expiration !== null ? `${lead.days_until_expiration}d` : '—'}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">License Status</span>
            <p className="text-gray-900 capitalize">{lead.license_status || '—'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Source</span>
            <p className="text-gray-900 capitalize">{lead.source}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Priority</span>
            <select
              value={lead.priority}
              onChange={(e) => handlePriorityChange(e.target.value)}
              className="text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 mt-0.5"
            >
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Progress</span>
            <select
              value={lead.work_state}
              onChange={(e) => handleWorkStateChange(e.target.value as LeadWorkState)}
              aria-label="Progress"
              className="w-full text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 mt-0.5"
            >
              {LEAD_WORK_STATES.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Assigned To</span>
            <UserSelect
              users={users}
              value={lead.assigned_to_user_id}
              onChange={handleAssigneeChange}
              ariaLabel="Assigned to"
              className="w-full text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 mt-0.5"
            />
          </div>
          <div>
            <span className="text-gray-500 text-xs uppercase">Added</span>
            <p className="text-gray-900">{formatLocalDate(lead.created_at)}</p>
          </div>
        </div>

        {/* Transmitter Location — where the radio actually is, which is not the
            licensee's mailing address. The parties themselves are the cards below. */}
        <div className="mt-4 border-t pt-4 text-sm">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Transmitter Location</h3>
          <div className="space-y-1.5">
            <DetailRow label="Address">
              {lead.transmitter_address || <span className="text-gray-400">—</span>}
            </DetailRow>
            <DetailRow label="City">
              {[lead.transmitter_city, lead.transmitter_state].filter(Boolean).join(', ') || <span className="text-gray-400">—</span>}
            </DetailRow>
            <DetailRow label="County">
              {lead.transmitter_county || <span className="text-gray-400">—</span>}
            </DetailRow>
          </div>
        </div>

        {/* Our own contact details, sitting immediately above the FCC's. Deliberately
            adjacent: the FCC address is often stale, and seeing ours next to theirs
            is what tells you the license record has drifted. */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-semibold text-gray-500 uppercase">
              Our contact details
            </h3>
            {!editingContact && (
              <button
                type="button"
                onClick={() => {
                  setContactForm({
                    contact_name: lead.contact_name ?? '',
                    contact_email: lead.contact_email ?? '',
                    contact_phone: lead.contact_phone ?? '',
                  })
                  setContactError(null)
                  setEditingContact(true)
                }}
                className="tap text-sm text-primary-600 hover:underline"
              >
                {lead.contact_email || lead.contact_name || lead.contact_phone ? 'Edit' : 'Add'}
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Who you actually reached, when the FCC's details are out of date. Used first
            when sending a message. Leave blank to fall back to the FCC's.
          </p>

          {editingContact ? (
            <div className="space-y-2">
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="Name"
                value={contactForm.contact_name}
                onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })}
              />
              <input
                type="email"
                className="input w-full text-sm"
                placeholder="Email address"
                value={contactForm.contact_email}
                onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
              />
              <input
                type="tel"
                className="input w-full text-sm"
                placeholder="Phone"
                value={contactForm.contact_phone}
                onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
              />
              {contactError && <p className="text-sm text-red-600">{contactError}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveContactDetails}
                  disabled={contactSaving}
                  className="btn btn-primary text-sm"
                >
                  {contactSaving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingContact(false); setContactError(null) }}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
              <DetailRow label="Name">
                {lead.contact_name || <span className="text-gray-400">—</span>}
              </DetailRow>
              <DetailRow label="Email">
                {lead.contact_email
                  ? <a href={`mailto:${lead.contact_email}`} className="text-primary-600 hover:underline">{lead.contact_email}</a>
                  : <span className="text-gray-400">—</span>}
              </DetailRow>
              <DetailRow label="Phone">
                {lead.contact_phone || <span className="text-gray-400">—</span>}
              </DetailRow>
            </div>
          )}
        </div>

        {fccParties.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">
              Contacts on file with the FCC
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              From this lead's license. The contact is the person or firm that files with the
              FCC on the licensee's behalf.
            </p>
            <div className="space-y-3">
              {fccParties.map((party, idx) => (
                <EntityCard
                  key={party.entity.id}
                  entity={party.entity}
                  isPrimary={idx === 0}
                />
              ))}
            </div>
          </div>
        )}

        {lead.search_description && (
          <p className="mt-3 text-xs text-gray-400 italic">From search: {lead.search_description}</p>
        )}
      </div>

      {/* Two-column: Notes + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Notes */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b">
            <h2 className="text-base font-semibold text-gray-900">
              Notes
              <span className="ml-2 text-xs font-normal text-gray-500">({notes.length})</span>
            </h2>
          </div>

          <div className="divide-y divide-gray-100">
            {notes.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No notes yet.</p>
            ) : (
              notes.map((n) => (
                <div key={n.id} className="px-5 py-3 group flex gap-3 items-start">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.note}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatLocalDateTime(n.created_at)}
                      {n.created_by && (
                        <span className="text-gray-300"> · {n.created_by.full_name ?? n.created_by.email}</span>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteNote(n.id)}
                    className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 rounded"
                    title="Delete note"
                    aria-label="Delete note"
                  >
                    <XIcon />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Add note */}
          <div className="px-5 py-4 border-t">
            {noteError && <p className="text-xs text-red-600 mb-2">{noteError}</p>}
            <div className="flex gap-2 items-end">
              <textarea
                rows={2}
                className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Add a note…"
                maxLength={5000}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button
                type="button"
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {addingNote ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="text-base font-semibold text-gray-900">
              Tasks
              {openTasks.length > 0 && (
                <span className="ml-2 text-xs font-normal text-orange-600">({openTasks.length} open)</span>
              )}
            </h2>
            <button
              onClick={() => { setShowAddTask(!showAddTask); setTaskError('') }}
              className="text-sm text-primary-600 hover:underline"
            >
              {showAddTask ? 'Cancel' : '+ Add'}
            </button>
          </div>

          {showAddTask && (
            <form onSubmit={handleAddTask} className="px-5 py-4 bg-gray-50 border-b space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  required
                  maxLength={200}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Due Date & Time</label>
                  <input
                    type="datetime-local"
                    value={taskForm.due_at}
                    onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as TaskPriority })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                <select
                  value={taskForm.assigned_to_user_id}
                  onChange={(e) => setTaskForm({ ...taskForm, assigned_to_user_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={String(u.id)}>{u.full_name || u.email}</option>
                  ))}
                </select>
              </div>
              {taskError && <p className="text-sm text-red-600">{taskError}</p>}
              <button
                type="submit"
                disabled={savingTask}
                className="w-full py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingTask ? 'Creating…' : 'Create Task'}
              </button>
            </form>
          )}

          <div className="divide-y divide-gray-100">
            {loadingTasks ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No tasks linked to this lead.</p>
            ) : (
              <>
                {openTasks.map((t) => (
                  <LeadTaskRow key={t.id} task={t} onStatusChange={handleTaskStatusChange} />
                ))}
                {doneTasks.length > 0 && openTasks.length > 0 && (
                  <div className="px-5 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                    Completed / Cancelled
                  </div>
                )}
                {doneTasks.map((t) => (
                  <LeadTaskRow key={t.id} task={t} onStatusChange={handleTaskStatusChange} />
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <ScheduledMessagesPanel
        leadId={id}
        refreshKey={scheduledRefresh}
        action={
          <button
            onClick={() => { setSendTemplateId(null); setShowSendMessage(true) }}
            className="tap text-sm text-primary-600 hover:underline"
          >
            Send a message
          </button>
        }
      />

      {showSendMessage && (
        <SendMessageModal
          onClose={() => setShowSendMessage(false)}
          leadId={id}
          defaultEmail={lead?.contact_email || lead?.entity_email || ''}
          defaultTemplateId={sendTemplateId}
          onSent={() => setScheduledRefresh((n) => n + 1)}
        />
      )}

      {showSendCard && (
        <SendCardModal
          onClose={() => setShowSendCard(false)}
          leadId={id}
          defaultEmail={lead?.contact_email || lead?.entity_email || ''}
          defaultSubject={lead?.call_sign ? `FCC License Renewal — ${lead.call_sign}` : ''}
          onSent={() => { loadLead(); setShowSendCard(false) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Task row helper
// ---------------------------------------------------------------------------

function LeadTaskRow({
  task,
  onStatusChange,
}: {
  task: Task
  onStatusChange: (task: Task, status: TaskStatus) => void
}) {
  const isOverdue =
    (task.due_at || task.due_date) &&
    task.status !== 'completed' &&
    task.status !== 'cancelled' &&
    new Date(task.due_at || task.due_date || '') < new Date()

  const dueLabel = task.due_at
    ? formatLocalDateTime(task.due_at)
    : task.due_date
    ? formatLocalDate(task.due_date)
    : null

  return (
    <div className={`px-5 py-3 ${isOverdue ? 'bg-red-50' : ''}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
            {task.title}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${TASK_PRIORITY_COLORS[task.priority]}`}>
              {task.priority}
            </span>
            {dueLabel && (
              <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                {isOverdue ? 'Overdue · ' : 'Due '}{dueLabel}
              </span>
            )}
            {task.assigned_to && (
              <span className="text-xs text-gray-400">→ {task.assigned_to.full_name || task.assigned_to.email}</span>
            )}
          </div>
        </div>
        <select
          value={task.status}
          onChange={(e) => onStatusChange(task, e.target.value as TaskStatus)}
          aria-label={`Status for task: ${task.title}`}
          className="text-xs border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 flex-shrink-0"
        >
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
    </div>
  )
}
